import { ReactNode } from 'react'

import { AnimatedText, AstraParagraph, astraTwMerge, MagicIcon } from '~/astra'
import { UserAvatar } from '~/components/UserAvatar'

import { LLMPreviewMessage } from './LLMPreviewMessage'
import { ChatMessage } from './types'

const LLMAvatar = () => (
    <span className="inline-flex h-7 w-7 shrink-0 select-none items-center justify-center self-start rounded-full bg-magic-strong-rest p-1">
        <MagicIcon className="text-inverted" />
    </span>
)
type AddMessageFunction = (message: ChatMessage) => void

const BaseChatEntry = ({
    className,
    children,
    avatar,
    direction,
}: {
    className?: string
    children: ReactNode
    avatar: ReactNode
    direction: 'left' | 'right'
}) => (
    <div
        className={astraTwMerge(
            'bg-primary-strong flex max-w-[80%] rounded-lg p-2 text-muted',
            className,
            direction === 'right' ? 'self-end' : '',
            direction === 'left' ? 'self-start' : ''
        )}
    >
        <span className={astraTwMerge('flex items-center gap-2', direction === 'right' ? 'flex-row-reverse' : '')}>
            {avatar}
            {children}
        </span>
    </div>
)

const UserChatEntry = ({ children }: { children: ReactNode }) => (
    <BaseChatEntry className="bg-neutral-subtle-rest" avatar={<UserAvatar />} direction="right">
        <AstraParagraph className="text-muted">{children}</AstraParagraph>
    </BaseChatEntry>
)

export const LLMChatEntry = ({ children }: { children: ReactNode }) => (
    <BaseChatEntry avatar={<LLMAvatar />} direction="left">
        {children}
    </BaseChatEntry>
)

const LoadingMessage = () => <AnimatedText variant="type" text="..." duration={3} className="text-primary-contrast text-lg" />

export const ChatEntry = ({ message, addMessage, onBackClick }: { message: ChatMessage; addMessage: AddMessageFunction; onBackClick: () => void }) => {
    switch (message.type) {
        case 'loading':
            return (
                <LLMChatEntry>
                    <LoadingMessage />
                </LLMChatEntry>
            )

        case 'LLMPreview':
            return (
                <LLMChatEntry>
                    <LLMPreviewMessage onBackClick={onBackClick} llmResponse={message.response} addMessage={addMessage} />
                </LLMChatEntry>
            )

        case 'LLMText':
            return <LLMChatEntry>{message.message}</LLMChatEntry>
        case 'userMessage':
            return <UserChatEntry>{message.message}</UserChatEntry>
        case 'error':
            return (
                <LLMChatEntry>
                    <AstraParagraph className="text-critical">Det oppstod en feil</AstraParagraph>
                </LLMChatEntry>
            )
        default: {
            // if this is a TS error, switch is missing a case for message.type
            message satisfies never
        }
    }
}
